/**
 * Professional onboarding — multi-step container.
 * Routes:
 *   /pro/apply             — Screen 0..5 (internal step state)
 *
 * Other terminal states are separate routes:
 *   /pro/submitted, /pro/verified, /pro/rejected
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, Search, Award, Users, Sparkles, BadgeCheck,
  Camera, Plus, Minus, FileText, X, Check, Upload,
} from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";
import RegistrationSheet from "../../components/common/RegistrationSheet";

const STEP_LABELS = [
  "Why FitFrik",
  "Get Started",
  "Your Profile",
  "Credentials",
  "Specialisation",
  "Preview",
];

const TRAINER_CERTS = [
  "ACSM", "NSCA", "ACE", "ISSA", "NASM",
  "CFT India", "YMCA", "K11 School of Fitness Sciences", "Others",
];
const NUTRI_QUALIFICATIONS = [
  "BSc Nutrition", "MSc Nutrition", "Registered Dietitian (RD)",
  "CNSC", "Diploma in Clinical Nutrition", "PG Diploma in Dietetics", "Others",
];
const TRAINER_SPECS = [
  "Weight Loss", "Muscle Building", "Strength and Powerlifting",
  "Functional Fitness", "Sports Performance", "Pre and Post Natal",
  "Senior Fitness", "Yoga", "Zumba", "Rehabilitation",
  "Bodyweight Training", "HIIT and Cardio",
];
const NUTRI_SPECS = [
  "Weight Management", "Sports Nutrition", "Clinical Nutrition",
  "Gut Health", "Diabetes and Metabolic Health", "PCOS and Hormonal Health",
  "Child and Adolescent Nutrition", "Pre and Post Natal Nutrition",
  "Plant Based Nutrition", "Eating Disorder Support",
];
const INDIAN_CITIES = [
  "Mumbai", "Pune", "Bengaluru", "Delhi", "Gurgaon", "Noida", "Hyderabad",
  "Chennai", "Kolkata", "Ahmedabad", "Jaipur", "Lucknow", "Chandigarh",
  "Indore", "Bhopal", "Kochi", "Thiruvananthapuram", "Guwahati", "Surat",
];

export default function ProfessionalOnboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const upgradeMode = location.state?.upgrade === true;

  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    role: null,
    full_name: upgradeMode && user ? user.name || "" : "",
    city: "",
    years_of_experience: 1,
    profile_photo_url: upgradeMode && user ? user.avatar_url : null,
    certification_body: "",
    qualification: "",
    certificate_number: "",
    registration_number: "",
    certificate_file_id: null,
    certificate_file_name: null,
    certificate_file_size: 0,
    specialisations: [],
    is_existing_user_upgrade: upgradeMode,
  });
  const [submitting, setSubmitting] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);

  const next = () => {
    // Auth gate — Steps 0,1,2 are public; Step 3 (Credentials, file upload) requires auth.
    if (step === 2 && !user) {
      setShowAuthGate(true);
      return;
    }
    setStep((s) => Math.min(5, s + 1));
  };
  // Back — from step > 0 goes to previous step, from step 0 exits the flow.
  const back = () => {
    if (step === 0) {
      navigate(-1);
    } else {
      setStep((s) => Math.max(0, s - 1));
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await apiClient.post("/professionals/apply", {
        role: data.role,
        full_name: data.full_name,
        city: data.city,
        years_of_experience: data.years_of_experience,
        profile_photo_url: data.profile_photo_url,
        certification_body: data.role === "trainer" ? data.certification_body : null,
        qualification: data.role === "nutritionist" ? data.qualification : null,
        certificate_number: data.role === "trainer" ? data.certificate_number : null,
        registration_number: data.role === "nutritionist" ? data.registration_number : null,
        certificate_file_id: data.certificate_file_id,
        specialisations: data.specialisations,
        is_existing_user_upgrade: data.is_existing_user_upgrade,
      });
      navigate("/pro/submitted", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  const pct = ((step + 1) / 6) * 100;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "#F4F3F1" }}
      data-testid="pro-onboarding"
    >
      {/* Top bar */}
      <header
        className="sticky top-0 z-20"
        style={{
          background: "#F4F3F1",
          borderBottom: "1px solid #E2E0DC",
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        <div className="max-w-2xl mx-auto h-14 px-4 flex items-center">
          <button
            onClick={back}
            data-testid="pro-back"
            className="-ml-2 w-10 h-10 grid place-items-center rounded-full hover:bg-white/60"
            aria-label="Back"
          >
            <ChevronLeft size={22} />
          </button>
          <h1
            className="font-barlow-cond uppercase mx-auto"
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#1A1A1A",
              letterSpacing: "0.01em",
              transform: "translateX(-20px)",
            }}
          >
            {STEP_LABELS[step]}
          </h1>
        </div>
        {/* Progress bar */}
        <div className="max-w-2xl mx-auto px-4 pb-2">
          <div style={{ height: 3, background: "#ECEAE7", borderRadius: 100 }}>
            <motion.div
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.3 }}
              style={{ height: 3, background: "#00D4FF", borderRadius: 100 }}
            />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-5 pt-6 pb-32">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -2 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
          >
            {step === 0 && <Step0 data={data} onContinue={next} />}
            {step === 1 && (
              <Step1
                data={data}
                onSelect={(role) => {
                  setData({ ...data, role });
                  setTimeout(next, 80);
                }}
              />
            )}
            {step === 2 && (
              <Step2 data={data} setData={setData} onContinue={next} />
            )}
            {step === 3 && (
              <Step3 data={data} setData={setData} onContinue={next} />
            )}
            {step === 4 && (
              <Step4 data={data} setData={setData} onContinue={next} />
            )}
            {step === 5 && (
              <Step5
                data={data}
                onSubmit={submit}
                onEdit={() => setStep(2)}
                submitting={submitting}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {showAuthGate && (
        <RegistrationSheet
          context="pro_apply"
          onSuccess={() => {
            setShowAuthGate(false);
            // Move to Step 3 (Credentials) now that the user is authenticated
            setStep(3);
            toast.success("Welcome! Let's verify your credentials.");
          }}
          onDismiss={() => setShowAuthGate(false)}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────
// STEP 0 — Value proposition
// ──────────────────────────────────────
function Step0({ data, onContinue }) {
  const navigate = useNavigate();
  return (
    <div data-testid="pro-step-0" className="space-y-7">
      <h1
        className="font-barlow-cond"
        style={{ fontSize: 40, fontWeight: 900, lineHeight: 0.95, color: "#1A1A1A" }}
      >
        Grow your practice with FitFrik
      </h1>

      {/* Stat tiles */}
      <div
        className="grid grid-cols-3"
        style={{
          background: "#FFFFFF",
          border: "1px solid #E2E0DC",
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        {[
          { num: "50K+", lbl: "MEMBERS" },
          { num: "40+", lbl: "CITIES" },
          { num: "Free", lbl: "TO JOIN" },
        ].map((s, i) => (
          <div
            key={s.lbl}
            className="text-center py-5"
            style={{ borderRight: i < 2 ? "1px solid #E2E0DC" : "none" }}
          >
            <div className="font-barlow-cond" style={{ fontSize: 32, fontWeight: 900, color: "#1A1A1A" }}>
              {s.num}
            </div>
            <div
              className="font-barlow uppercase mt-1"
              style={{ fontSize: 11, fontWeight: 600, color: "#8E8A84", letterSpacing: "0.08em" }}
            >
              {s.lbl}
            </div>
          </div>
        ))}
      </div>

      {/* Benefits */}
      <div className="space-y-4">
        <Benefit
          icon={Search}
          title="Direct member discovery"
          body="Members in your city find you by specialisation and availability."
        />
        <Benefit
          icon={BadgeCheck}
          title="Verified badge on your profile"
          body="Stand out with a credential-verified badge that members trust."
        />
        <Benefit
          icon={Award}
          title="Zero commission to start"
          body="List your profile and get discovered at no cost."
        />
      </div>

      <button
        onClick={onContinue}
        data-testid="step0-cta"
        className="w-full mt-3"
        style={{
          background: "#00D4FF",
          color: "#000",
          height: 52,
          borderRadius: 100,
          fontFamily: "Manrope",
          fontWeight: 700,
          fontSize: 15,
        }}
      >
        Apply Now — It's Free
      </button>

      <p
        className="text-center"
        style={{ fontSize: 12, fontWeight: 500, color: "#8E8A84" }}
      >
        Already have a FitFrik account?{" "}
        <button
          onClick={() => navigate("/pro/apply", { state: { upgrade: true }, replace: true })}
          style={{ color: "#1A1A1A", textDecoration: "underline" }}
        >
          Upgrade your profile
        </button>
      </p>
    </div>
  );
}

function Benefit({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 grid place-items-center rounded-full mt-0.5 shrink-0"
        style={{ background: "rgba(0, 212, 255, 0.12)" }}>
        <Icon size={18} color="#00B0D5" strokeWidth={2} />
      </div>
      <div>
        <div className="font-barlow" style={{ fontSize: 15, fontWeight: 700, color: "#1A1A1A" }}>
          {title}
        </div>
        <div className="font-barlow" style={{ fontSize: 13, fontWeight: 400, color: "#6B6760", marginTop: 2 }}>
          {body}
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────
// STEP 1 — Role select
// ──────────────────────────────────────
function Step1({ data, onSelect }) {
  return (
    <div data-testid="pro-step-1" className="space-y-6">
      <h1 className="font-barlow-cond" style={{ fontSize: 40, fontWeight: 900, color: "#1A1A1A" }}>
        I am a
      </h1>
      <div className="space-y-3">
        {[
          { id: "trainer", title: "Certified Trainer", desc: "Gym, home, yoga, zumba, or sports coaching" },
          { id: "nutritionist", title: "Certified Nutritionist", desc: "Diet planning, clinical nutrition, sports nutrition" },
        ].map((c) => {
          const sel = data.role === c.id;
          return (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              data-testid={`role-${c.id}`}
              className="w-full text-left transition-all"
              style={{
                background: sel ? "rgba(0, 212, 255, 0.12)" : "#FFFFFF",
                border: `1px solid ${sel ? "#00D4FF" : "#E2E0DC"}`,
                borderRadius: 16,
                padding: "24px 20px",
              }}
            >
              <div className="font-barlow-cond" style={{ fontSize: 24, fontWeight: 800, color: "#1A1A1A" }}>
                {c.title}
              </div>
              <div className="font-barlow mt-1" style={{ fontSize: 14, fontWeight: 400, color: "#6B6760" }}>
                {c.desc}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────
// STEP 2 — Basic info
// ──────────────────────────────────────
function Step2({ data, setData, onContinue }) {
  const [cityFocused, setCityFocused] = useState(false);
  const photoInput = useRef(null);

  const onPhoto = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setData({ ...data, profile_photo_url: reader.result });
    reader.readAsDataURL(file);
  };

  const cityMatches = useMemo(
    () => INDIAN_CITIES.filter((c) => c.toLowerCase().startsWith((data.city || "").toLowerCase())).slice(0, 6),
    [data.city]
  );

  const valid = data.full_name.trim().length >= 2 && data.city.trim().length >= 2;

  return (
    <div data-testid="pro-step-2" className="space-y-7">
      <h1 className="font-barlow-cond" style={{ fontSize: 36, fontWeight: 900, color: "#1A1A1A" }}>
        Tell us about yourself
      </h1>

      <Field label="FULL NAME">
        <Input
          value={data.full_name}
          onChange={(e) => setData({ ...data, full_name: e.target.value })}
          placeholder="As on your certificate"
          testid="pro-name"
        />
      </Field>

      <Field label="CITY">
        <div className="relative">
          <Input
            value={data.city}
            onChange={(e) => setData({ ...data, city: e.target.value })}
            placeholder="Your primary city"
            onFocus={() => setCityFocused(true)}
            onBlur={() => setTimeout(() => setCityFocused(false), 150)}
            testid="pro-city"
          />
          {cityFocused && data.city && cityMatches.length > 0 && (
            <div
              className="absolute left-0 right-0 top-full mt-1 z-30"
              style={{
                background: "#FFFFFF",
                border: "1px solid #E2E0DC",
                borderRadius: 12,
                boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
              }}
            >
              {cityMatches.map((c) => (
                <button
                  key={c}
                  onMouseDown={() => setData({ ...data, city: c })}
                  className="block w-full text-left px-3 py-2.5 hover:bg-canvas2 font-barlow"
                  style={{ fontSize: 14, color: "#1A1A1A" }}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </Field>

      <Field label="YEARS OF EXPERIENCE">
        <div
          className="flex items-center justify-between px-2"
          style={{ background: "#ECEAE7", border: "1px solid #E2E0DC", borderRadius: 12, height: 52 }}
        >
          <button
            onClick={() => setData({ ...data, years_of_experience: Math.max(0, data.years_of_experience - 1) })}
            className="w-11 h-11 grid place-items-center"
            style={{ color: "#6B6760" }}
            data-testid="years-minus"
            aria-label="Decrease"
          >
            <Minus size={18} />
          </button>
          <div
            className="font-barlow-cond text-center flex-1"
            style={{ fontSize: 22, fontWeight: 800, color: "#1A1A1A" }}
            data-testid="years-value"
          >
            {data.years_of_experience} {data.years_of_experience === 1 ? "year" : "years"}
          </div>
          <button
            onClick={() => setData({ ...data, years_of_experience: Math.min(40, data.years_of_experience + 1) })}
            className="w-11 h-11 grid place-items-center"
            style={{ color: "#6B6760" }}
            data-testid="years-plus"
            aria-label="Increase"
          >
            <Plus size={18} />
          </button>
        </div>
      </Field>

      <Field
        label="PROFILE PHOTO"
        sub={
          <span style={{ fontSize: 12, fontWeight: 400, color: "#8E8A84" }}>
            Optional — you can add this later
          </span>
        }
      >
        <button
          onClick={() => photoInput.current?.click()}
          data-testid="pro-photo-btn"
          className="grid place-items-center"
          style={{
            width: 80, height: 80, borderRadius: "50%",
            background: data.profile_photo_url ? "transparent" : "#ECEAE7",
            border: data.profile_photo_url ? "none" : "1px dashed #C7C4BF",
            overflow: "hidden",
          }}
        >
          {data.profile_photo_url ? (
            <img src={data.profile_photo_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <Camera size={22} color="#8E8A84" />
          )}
        </button>
        <input
          ref={photoInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onPhoto(e.target.files?.[0])}
          data-testid="pro-photo-input"
        />
      </Field>

      <FloatingPrimary
        label="Continue"
        onClick={onContinue}
        disabled={!valid}
        testid="step2-cta"
      />
    </div>
  );
}

// ──────────────────────────────────────
// STEP 3 — Credentials (with file upload)
// ──────────────────────────────────────
function Step3({ data, setData, onContinue }) {
  const isTrainer = data.role === "trainer";
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInput = useRef(null);

  const ALLOWED = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
  const MAX = 10 * 1024 * 1024;

  const onFile = (file) => {
    if (!file) return;
    setError(null);
    if (!ALLOWED.includes(file.type)) {
      setError("Only JPG, PNG or PDF files are accepted.");
      return;
    }
    if (file.size > MAX) {
      setError("File is too large. Please upload under 10MB.");
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data: res } = await apiClient.post("/professionals/upload-certificate", {
          filename: file.name,
          content_type: file.type,
          base64_data: reader.result,
        });
        setData({
          ...data,
          certificate_file_id: res.file_id,
          certificate_file_name: res.filename,
          certificate_file_size: res.size_bytes,
        });
      } catch (e) {
        setError(e.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const removeFile = () => {
    setData({
      ...data,
      certificate_file_id: null,
      certificate_file_name: null,
      certificate_file_size: 0,
    });
    setError(null);
  };

  const valid =
    !!data.certificate_file_id &&
    (isTrainer ? !!data.certification_body && !!data.certificate_number : !!data.qualification);

  return (
    <div data-testid="pro-step-3" className="space-y-6">
      <div>
        <h1 className="font-barlow-cond" style={{ fontSize: 36, fontWeight: 900, color: "#1A1A1A" }}>
          Your credentials
        </h1>
        <p className="font-barlow mt-2" style={{ fontSize: 14, fontWeight: 400, color: "#6B6760" }}>
          We verify every professional on FitFrik
        </p>
      </div>

      {isTrainer ? (
        <>
          <Field label="CERTIFICATION">
            <Select
              value={data.certification_body}
              onChange={(v) => setData({ ...data, certification_body: v })}
              options={TRAINER_CERTS}
              placeholder="Select your certification"
              testid="cert-body"
            />
          </Field>
          <Field label="CERTIFICATE NUMBER">
            <Input
              value={data.certificate_number}
              onChange={(e) => setData({ ...data, certificate_number: e.target.value })}
              placeholder="As printed on your certificate"
              testid="cert-number"
            />
          </Field>
        </>
      ) : (
        <>
          <Field label="QUALIFICATION">
            <Select
              value={data.qualification}
              onChange={(v) => setData({ ...data, qualification: v })}
              options={NUTRI_QUALIFICATIONS}
              placeholder="Select your qualification"
              testid="qualification"
            />
          </Field>
          <Field
            label="REGISTRATION NUMBER"
            sub={<span style={{ fontSize: 12, fontWeight: 400, color: "#8E8A84" }}>Optional</span>}
          >
            <Input
              value={data.registration_number}
              onChange={(e) => setData({ ...data, registration_number: e.target.value })}
              placeholder="IDA or state council number if applicable"
              testid="reg-number"
            />
          </Field>
        </>
      )}

      <Field
        label={isTrainer ? "UPLOAD CERTIFICATE" : "UPLOAD DEGREE OR CERTIFICATE"}
        sub={<span style={{ fontSize: 12, fontWeight: 400, color: "#8E8A84" }}>JPG, PNG or PDF — max 10MB</span>}
      >
        {!data.certificate_file_id ? (
          <button
            onClick={() => fileInput.current?.click()}
            data-testid="cert-upload-btn"
            className="w-full grid place-items-center"
            style={{
              background: "#ECEAE7",
              border: "1px dashed #C7C4BF",
              borderRadius: 12,
              height: 100,
            }}
          >
            <div className="text-center">
              <Upload size={20} color="#8E8A84" className="mx-auto" />
              <div className="font-barlow mt-2" style={{ fontSize: 13, fontWeight: 600, color: "#8E8A84" }}>
                {uploading ? "Uploading…" : "Tap to upload"}
              </div>
            </div>
          </button>
        ) : (
          <div
            className="flex items-center gap-3 p-3"
            style={{ background: "#FFFFFF", border: "1px solid #E2E0DC", borderRadius: 12 }}
            data-testid="cert-uploaded"
          >
            <FileText size={20} color="#00B0D5" />
            <div className="flex-1 min-w-0">
              <div className="font-barlow truncate" style={{ fontSize: 14, fontWeight: 600, color: "#1A1A1A" }}>
                {data.certificate_file_name}
              </div>
              <div className="font-barlow" style={{ fontSize: 12, color: "#8E8A84" }}>
                {(data.certificate_file_size / 1024).toFixed(0)} KB
              </div>
            </div>
            <button
              onClick={removeFile}
              data-testid="cert-remove"
              className="font-barlow"
              style={{ fontSize: 13, color: "#6B6760", textDecoration: "underline" }}
            >
              Remove
            </button>
          </div>
        )}
        <input
          ref={fileInput}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="cert-file-input"
        />
        {error && (
          <div
            className="font-barlow mt-2"
            data-testid="cert-error"
            style={{ fontSize: 12, fontWeight: 600, color: "#E5484D" }}
          >
            {error}
          </div>
        )}
      </Field>

      <FloatingPrimary
        label="Continue"
        onClick={onContinue}
        disabled={!valid || uploading}
        testid="step3-cta"
      />
    </div>
  );
}

// ──────────────────────────────────────
// STEP 4 — Specialisation
// ──────────────────────────────────────
function Step4({ data, setData, onContinue }) {
  const isTrainer = data.role === "trainer";
  const tags = isTrainer ? TRAINER_SPECS : NUTRI_SPECS;

  const toggle = (t) => {
    const set = new Set(data.specialisations);
    set.has(t) ? set.delete(t) : set.add(t);
    setData({ ...data, specialisations: [...set] });
  };

  const valid = data.specialisations.length >= 1;

  return (
    <div data-testid="pro-step-4" className="space-y-6">
      <div>
        <h1 className="font-barlow-cond" style={{ fontSize: 36, fontWeight: 900, color: "#1A1A1A" }}>
          What do you specialise in
        </h1>
        <p className="font-barlow mt-2" style={{ fontSize: 14, fontWeight: 400, color: "#6B6760" }}>
          Select all that apply
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="spec-tags">
        {tags.map((t) => {
          const on = data.specialisations.includes(t);
          return (
            <button
              key={t}
              onClick={() => toggle(t)}
              data-testid={`spec-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="font-barlow"
              style={{
                background: on ? "#00D4FF" : "#ECEAE7",
                border: on ? "none" : "1px solid #E2E0DC",
                color: on ? "#F4F3F1" : "#6B6760",
                fontSize: 13,
                fontWeight: on ? 700 : 600,
                height: 36,
                borderRadius: 100,
                padding: "0 16px",
              }}
            >
              {t}
            </button>
          );
        })}
      </div>

      <FloatingPrimary
        label="Continue"
        onClick={onContinue}
        disabled={!valid}
        testid="step4-cta"
      />
    </div>
  );
}

// ──────────────────────────────────────
// STEP 5 — Profile preview + submit
// ──────────────────────────────────────
function Step5({ data, onSubmit, onEdit, submitting }) {
  const initials = data.full_name
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const roleLabel = data.role === "trainer" ? "Certified Trainer" : "Certified Nutritionist";

  return (
    <div data-testid="pro-step-5" className="space-y-6">
      <div>
        <h1 className="font-barlow-cond" style={{ fontSize: 36, fontWeight: 900, color: "#1A1A1A" }}>
          Your profile preview
        </h1>
        <p className="font-barlow mt-2" style={{ fontSize: 14, fontWeight: 400, color: "#6B6760" }}>
          This is how you appear to FitFrik members
        </p>
      </div>

      <div
        style={{
          background: "#FFFFFF",
          border: "1px solid #E2E0DC",
          borderRadius: 16,
          padding: 20,
        }}
        data-testid="preview-card"
      >
        <div className="flex items-center gap-4">
          {data.profile_photo_url ? (
            <img
              src={data.profile_photo_url}
              alt=""
              className="rounded-full object-cover"
              style={{ width: 64, height: 64 }}
            />
          ) : (
            <div
              className="rounded-full grid place-items-center font-barlow-cond"
              style={{
                width: 64, height: 64, background: "#ECEAE7",
                fontSize: 22, fontWeight: 800, color: "#6B6760",
              }}
            >
              {initials || "FF"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-barlow-cond truncate" style={{ fontSize: 22, fontWeight: 800, color: "#1A1A1A" }}>
              {data.full_name}
            </div>
            <span
              className="inline-block mt-1.5 font-barlow"
              style={{
                background: "#00D4FF", color: "#F4F3F1",
                fontSize: 11, fontWeight: 700,
                padding: "4px 10px", borderRadius: 100,
              }}
            >
              {roleLabel}
            </span>
          </div>
        </div>

        <div className="mt-4 font-barlow" style={{ fontSize: 13, fontWeight: 500, color: "#8E8A84" }}>
          {data.city} · {data.years_of_experience} {data.years_of_experience === 1 ? "year" : "years"} experience
        </div>

        {data.specialisations.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {data.specialisations.map((s) => (
              <span
                key={s}
                className="font-barlow"
                style={{
                  background: "#ECEAE7", border: "1px solid #E2E0DC",
                  fontSize: 12, fontWeight: 600, color: "#6B6760",
                  padding: "4px 10px", borderRadius: 100,
                }}
              >
                {s}
              </span>
            ))}
          </div>
        )}
      </div>

      <p className="font-barlow" style={{ fontSize: 13, fontWeight: 400, color: "#8E8A84" }}>
        Your certificate will be reviewed by our team before your profile goes live.
      </p>

      <div className="space-y-2">
        <button
          onClick={onSubmit}
          disabled={submitting}
          data-testid="step5-submit"
          className="w-full disabled:opacity-50"
          style={{
            background: "#00D4FF", color: "#000",
            height: 52, borderRadius: 100,
            fontFamily: "Manrope", fontWeight: 700, fontSize: 15,
          }}
        >
          {submitting ? "Submitting…" : "Submit for Verification"}
        </button>
        <button
          onClick={onEdit}
          data-testid="step5-edit"
          className="w-full"
          style={{
            background: "transparent", color: "#1A1A1A",
            border: "1px solid #E2E0DC",
            height: 48, borderRadius: 100,
            fontFamily: "Manrope", fontWeight: 600, fontSize: 14,
          }}
        >
          Edit Details
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────
// Reusable bits
// ──────────────────────────────────────
function Field({ label, sub, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label
          className="font-barlow uppercase"
          style={{ fontSize: 11, fontWeight: 700, color: "#8E8A84", letterSpacing: "0.08em" }}
        >
          {label}
        </label>
        {sub}
      </div>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, onFocus, onBlur, testid }) {
  return (
    <input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onFocus={onFocus}
      onBlur={onBlur}
      data-testid={testid}
      className="w-full font-barlow outline-none"
      style={{
        background: "#ECEAE7",
        border: "1px solid #E2E0DC",
        borderRadius: 12,
        height: 52,
        padding: "0 14px",
        fontSize: 15,
        fontWeight: 500,
        color: "#1A1A1A",
      }}
    />
  );
}

function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full font-barlow outline-none appearance-none"
      style={{
        background: "#ECEAE7",
        border: "1px solid #E2E0DC",
        borderRadius: 12,
        height: 52,
        padding: "0 14px",
        fontSize: 15,
        fontWeight: 500,
        color: value ? "#1A1A1A" : "#8E8A84",
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236B6760' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 14px center",
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function FloatingPrimary({ label, onClick, disabled, testid }) {
  return (
    <div
      className="fixed bottom-0 left-0 right-0 px-5"
      style={{
        paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)",
        paddingTop: 28,
        background: "linear-gradient(to top, #F4F3F1 60%, rgba(244,243,241,0))",
        zIndex: 30,
      }}
    >
      <button
        onClick={onClick}
        disabled={disabled}
        data-testid={testid}
        className="w-full max-w-2xl mx-auto disabled:opacity-50"
        style={{
          background: "#00D4FF", color: "#000",
          height: 52, borderRadius: 100,
          fontFamily: "Manrope", fontWeight: 700, fontSize: 15,
        }}
      >
        {label}
      </button>
    </div>
  );
}
